/**
 * SpacesListPanel - Slide-out panel listing areas and units
 *
 * Opens from the left edge. Shows buildings grouped by area,
 * with units nested under each. Includes occupancy indicators.
 */

import { useMemo } from 'react'
import { Building2, DoorOpen, Plane } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useRampPanelStore } from '@/stores/rampPanelStore'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface AreaInfo {
  id: string
  name: string
  areaCode: string
  type: string
}

interface UnitInfo {
  id: string
  unitCode: string
  areaId: string
  type: string | null
}

interface SpacesListPanelProps {
  areas: AreaInfo[]
  units: UnitInfo[]
  enrichedData: Map<string, AllocationEnrichedData>
  onFocusArea: (areaId: string) => void
}

export function SpacesListPanel({ areas, units, enrichedData, onFocusArea }: SpacesListPanelProps) {
  const { activePanel, closePanel } = useRampPanelStore()
  const isOpen = activePanel === 'spaces-list'

  // Build occupancy map: unitId → aircraft tail numbers
  const unitOccupancy = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const [, data] of enrichedData) {
      const unitId = data.allocation.unitId
      if (unitId) {
        const existing = map.get(unitId) || []
        existing.push(data.aircraft.tailNumber)
        map.set(unitId, existing)
      }
    }
    return map
  }, [enrichedData])

  // Group units by area
  const areaUnits = useMemo(() => {
    const grouped = new Map<string, UnitInfo[]>()
    for (const unit of units) {
      const existing = grouped.get(unit.areaId) || []
      existing.push(unit)
      grouped.set(unit.areaId, existing)
    }
    return grouped
  }, [units])

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closePanel()}>
      <SheetContent side="left" className="w-[360px] p-0">
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-4 h-4" />
            Spaces
            <Badge variant="secondary" className="ml-auto text-xs">
              {areas.length} areas · {units.length} units
            </Badge>
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-80px)]">
          <div className="divide-y">
            {areas.map((area) => {
              const areaUnitList = areaUnits.get(area.id) || []
              const occupiedCount = areaUnitList.filter(
                (u) => (unitOccupancy.get(u.id)?.length ?? 0) > 0
              ).length
              const totalCount = areaUnitList.length
              const occupancyPct = totalCount > 0
                ? Math.round((occupiedCount / totalCount) * 100)
                : 0

              return (
                <div key={area.id}>
                  {/* Area header */}
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors"
                    onClick={() => {
                      onFocusArea(area.id)
                      closePanel()
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium text-sm">{area.name}</span>
                        <span className="text-xs text-muted-foreground">{area.areaCode}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {occupiedCount}/{totalCount}
                        </span>
                        <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full transition-all',
                              occupancyPct >= 90 ? 'bg-red-500' :
                              occupancyPct >= 70 ? 'bg-amber-500' :
                              'bg-green-500'
                            )}
                            style={{ width: `${occupancyPct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Units under this area */}
                  {areaUnitList.length > 0 && (
                    <div className="pb-1">
                      {areaUnitList.map((unit) => {
                        const aircraft = unitOccupancy.get(unit.id) || []
                        const isOccupied = aircraft.length > 0

                        return (
                          <div
                            key={unit.id}
                            className="flex items-center gap-2 px-4 pl-10 py-1.5 text-xs"
                          >
                            <DoorOpen className={cn(
                              'w-3.5 h-3.5',
                              isOccupied ? 'text-blue-500' : 'text-muted-foreground/50'
                            )} />
                            <span className={cn(
                              'font-mono',
                              isOccupied ? 'text-foreground' : 'text-muted-foreground'
                            )}>
                              {unit.unitCode}
                            </span>
                            {isOccupied && (
                              <span className="text-muted-foreground flex items-center gap-1">
                                <Plane className="w-3 h-3" />
                                {aircraft.join(', ')}
                              </span>
                            )}
                            {!isOccupied && (
                              <span className="text-muted-foreground/50">Empty</span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}

            {areas.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No spaces configured
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
